import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { useSEO } from '../../utils/seo';

const schedule = [
  { day: 'Monday',    hours: '8:00 AM – 3:00 PM',  doctors: 'Dr. Martis' },
  { day: 'Tuesday',   hours: '8:00 AM – 4:00 PM',  doctors: 'Dr. Taghaddos, Dr. Lao' },
  { day: 'Wednesday', hours: '8:00 AM – 5:00 PM',  doctors: 'Dr. Taghaddos, Dr. Lao, Dr. Salama, Dr. Martis (9am–3pm)' },
  { day: 'Thursday',  hours: '8:00 AM – 4:00 PM',  doctors: 'Dr. Martis, Dr. Lao' },
  { day: 'Friday',    hours: '8:00 AM – 1:00 PM',  doctors: 'Dr. Taghaddos' },
  { day: 'Saturday',  hours: '9:00 AM – 12:00 PM', doctors: 'Dr. Taghaddos, Dr. Martis' },
  { day: 'Sunday',    hours: '9:00 AM – 11:00 AM', doctors: 'Dr. Martis (10am–12pm)' },
];

const serviceAreas = ['Thornhill', 'North York', 'Richmond Hill', 'Vaughan', 'Toronto'];

export default function LocationPage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://thornhilltreatment.com';

  useSEO({
    title: 'Location & Hours | Thornhill Drug-Mart | 8108 Yonge St, Thornhill ON',
    description:
      'Visit Thornhill Drug-Mart at 8108 Yonge St Unit 3-4, Thornhill, ON L4J 1W4. Open Monday–Friday 9am–6pm, Saturday 10am–4pm. Free parking available. Call (905) 709-9985 for directions.',
    keywords:
      'Thornhill Drug-Mart location, pharmacy hours Thornhill, 8108 Yonge Street, Thornhill pharmacy directions, pharmacy near me',
    canonical: `${siteUrl}/location`,
    ogType: 'website',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Pharmacy',
      name: 'Thornhill Drug-Mart',
      description: 'Professional pharmacy providing addiction treatment services and pharmacy care in Thornhill, Ontario',
      url: `${siteUrl}/location`,
      telephone: '+1-905-709-9985',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '8108 Yonge St Unit 3-4',
        addressLocality: 'Thornhill',
        addressRegion: 'ON',
        postalCode: 'L4J 1W4',
        addressCountry: 'CA',
      },
      geo: { '@type': 'GeoCoordinates', latitude: '43.8184', longitude: '-79.4278' },
    },
  });

  const getCurrentDay = () => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[new Date().getDay()];
  };
  const currentDay = getCurrentDay();

  return (
    <div className="min-h-screen bg-[#f8f8f7]">
      <Navbar isScrolled={isScrolled} />

      {/* ── Hero Banner ── */}
      <section className="relative w-full overflow-hidden" style={{ minHeight: '320px' }}>
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=modern%20professional%20pharmacy%20interior%20clean%20organized%20prescription%20counter%20warm%20teal%20white%20tones%20bright%20lighting%20welcoming%20healthcare%20environment%20wide%20angle%20professional%20medical%20facility%20Thornhill%20Ontario&width=1920&height=400&seq=loc-hero-v3&orientation=landscape"
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover object-center"
            width={1920}
            height={400}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/75 to-gray-900/60" />
        </div>

        <div
          className="relative z-10 max-w-7xl mx-auto px-6 flex flex-col justify-center"
          style={{ minHeight: '320px', paddingTop: '128px', paddingBottom: '48px' }}
        >
          <nav aria-label="Breadcrumb" className="mb-5">
            <ol className="flex items-center space-x-2 text-sm">
              <li><Link to="/" className="text-white/70 hover:text-white transition-colors">Home</Link></li>
              <li aria-hidden="true"><i className="ri-arrow-right-s-line text-white/50"></i></li>
              <li><span className="text-teal-400 font-medium">Location &amp; Hours</span></li>
            </ol>
          </nav>

          <motion.h1
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight max-w-2xl"
          >
            Location &amp; Hours
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15, ease: 'easeOut' }}
            className="mt-4 text-lg text-white/80 max-w-xl"
          >
            8108 Yonge St Unit 3-4, Thornhill, ON — open 7 days a week
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3, ease: 'easeOut' }}
            className="mt-8 flex flex-wrap gap-4"
          >
            <a
              href="https://www.google.com/maps/dir/?api=1&destination=8108+Yonge+St+Unit+3-4,+Thornhill,+ON+L4J+1W4"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-semibold px-7 py-3 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-navigation-line"></i>
              Get Directions
            </a>
            <a
              href="tel:9057099985"
              className="inline-flex items-center gap-2 border border-white/40 hover:border-white text-white font-semibold px-7 py-3 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line"></i>
              (905) 709-9985
            </a>
          </motion.div>
        </div>
      </section>

      {/* ── Main 2-column layout ── */}
      <div className="max-w-7xl mx-auto px-6 py-14">
        <div className="flex flex-col lg:flex-row gap-12 items-start">

          {/* ── Left: Main Content ── */}
          <main className="flex-1 min-w-0 space-y-10">

            {/* Contact Info Cards */}
            <section aria-labelledby="contact-cards-heading">
              <p className="text-xs font-semibold text-teal-600 uppercase tracking-[0.15em] mb-4">
                Contact Information
              </p>
              <h2 id="contact-cards-heading" className="text-2xl font-bold text-gray-900 mb-6">
                Reach Us Anytime
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { icon: 'ri-map-pin-line',  title: 'Address', lines: ['8108 Yonge St Unit 3-4', 'Thornhill, ON L4J 1W4', 'Canada'] },
                  { icon: 'ri-phone-line',    title: 'Phone',   lines: ['(905) 709-9985'] },
                  { icon: 'ri-printer-line',  title: 'Fax',     lines: ['(905) 709-9986'] },
                  { icon: 'ri-mail-line',     title: 'Email',   lines: ['info@thornhilltreatment.com'] },
                ].map((card) => (
                  <div key={card.title} className="bg-white border border-gray-200 rounded-lg p-5 flex items-start gap-4">
                    <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className={`${card.icon} text-teal-600 text-lg`}></i>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">{card.title}</p>
                      {card.title === 'Phone' ? (
                        <a href="tel:9057099985" className="text-sm font-semibold text-teal-600 hover:text-teal-700 cursor-pointer">{card.lines[0]}</a>
                      ) : card.title === 'Email' ? (
                        <a href="mailto:info@thornhilltreatment.com" className="text-sm font-semibold text-teal-600 hover:text-teal-700 cursor-pointer break-all">{card.lines[0]}</a>
                      ) : (
                        card.lines.map((l, i) => (
                          <p key={i} className="text-sm text-gray-700 leading-snug">{l}</p>
                        ))
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Map */}
            <section aria-labelledby="map-heading">
              <p className="text-xs font-semibold text-teal-600 uppercase tracking-[0.15em] mb-2">
                Find Us
              </p>
              <h2 id="map-heading" className="text-2xl font-bold text-gray-900 mb-5">
                Our Location
              </h2>
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="aspect-video w-full">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2879.8234567890123!2d-79.4278!3d43.8184!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDPCsDQ5JzA2LjIiTiA3OcKwMjUnNDAuMSJX!5e0!3m2!1sen!2sca!4v1234567890123"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Thornhill Drug-Mart Location"
                  ></iframe>
                </div>
                <div className="px-6 py-4 border-t border-gray-100 flex flex-col sm:flex-row gap-3">
                  <a
                    href="https://www.google.com/maps/dir/?api=1&destination=8108+Yonge+St+Unit+3-4,+Thornhill,+ON+L4J+1W4"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold px-5 py-2.5 rounded-md transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-navigation-line"></i>
                    Get Directions
                  </a>
                  <a
                    href="tel:9057099985"
                    className="flex-1 inline-flex items-center justify-center gap-2 border border-gray-200 hover:border-teal-500 text-gray-700 hover:text-teal-600 text-sm font-semibold px-5 py-2.5 rounded-md transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-phone-line"></i>
                    Call Now
                  </a>
                </div>
              </div>
            </section>

            {/* Schedule */}
            <section aria-labelledby="schedule-heading">
              <p className="text-xs font-semibold text-teal-600 uppercase tracking-[0.15em] mb-2">
                Doctor Schedule
              </p>
              <h2 id="schedule-heading" className="text-2xl font-bold text-gray-900 mb-5">
                Operating Hours
              </h2>

              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden divide-y divide-gray-100">
                {schedule.map((item) => {
                  const isToday = currentDay === item.day;
                  return (
                    <div
                      key={item.day}
                      className={`flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 px-5 py-4 transition-colors ${isToday ? 'bg-teal-50' : 'hover:bg-gray-50'}`}
                    >
                      <div className="flex items-center gap-3 w-36 flex-shrink-0">
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${isToday ? 'bg-teal-500' : 'bg-gray-300'}`}></div>
                        <span className={`text-sm font-bold ${isToday ? 'text-teal-700' : 'text-gray-800'}`}>
                          {item.day}
                          {isToday && (
                            <span className="ml-2 text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full font-medium">Today</span>
                          )}
                        </span>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-4 sm:gap-8 text-sm text-gray-600 flex-1">
                        <span className="flex items-center gap-1.5">
                          <i className="ri-time-line text-teal-500 text-xs"></i>
                          {item.hours}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <i className="ri-stethoscope-line text-teal-500 text-xs"></i>
                          {item.doctors}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-4 bg-white border border-teal-200 rounded-lg px-5 py-4 flex items-start gap-3">
                <div className="w-8 h-8 bg-teal-600 rounded-md flex items-center justify-center flex-shrink-0">
                  <i className="ri-information-line text-white text-base"></i>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">
                  Doctor schedules may vary. Please call ahead at{' '}
                  <a href="tel:9057099985" className="text-teal-600 hover:text-teal-700 font-semibold cursor-pointer">(905) 709-9985</a>{' '}
                  to confirm availability and book your appointment.
                </p>
              </div>
            </section>

            {/* Parking & Accessibility */}
            <section aria-labelledby="access-heading">
              <p className="text-xs font-semibold text-teal-600 uppercase tracking-[0.15em] mb-2">
                Accessibility
              </p>
              <h2 id="access-heading" className="text-2xl font-bold text-gray-900 mb-5">
                Parking &amp; Access
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { icon: 'ri-parking-box-line', title: 'Free Parking',          desc: 'Ample free parking on-site for all visitors.' },
                  { icon: 'ri-wheelchair-line',  title: 'Wheelchair Accessible', desc: 'Fully accessible facility with ramps and elevators.' },
                  { icon: 'ri-bus-line',          title: 'Public Transit',         desc: 'Conveniently located near major bus routes on Yonge St.' },
                ].map((item) => (
                  <div key={item.title} className="bg-white border border-gray-200 rounded-lg p-5">
                    <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center mb-3">
                      <i className={`${item.icon} text-teal-600 text-xl`}></i>
                    </div>
                    <h3 className="text-sm font-bold text-gray-900 mb-1">{item.title}</h3>
                    <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </section>
          </main>

          {/* ── Right: Sticky Sidebar ── */}
          <aside className="sticky top-24 space-y-5 w-full lg:w-72 flex-shrink-0">

            {/* Areas We Serve */}
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-900 px-5 py-4">
                <h2 className="text-xs font-bold text-white tracking-[0.12em] uppercase">Areas We Serve</h2>
              </div>
              <ul className="divide-y divide-gray-100">
                {serviceAreas.map((area) => (
                  <li key={area} className="flex items-center justify-between px-5 py-3 text-sm text-gray-600 font-medium hover:bg-gray-50 transition-colors">
                    <span className="flex items-center gap-2">
                      <i className="ri-map-pin-2-fill text-teal-500 text-xs"></i>
                      {area}
                    </span>
                    <i className="ri-arrow-right-line text-gray-300 text-sm"></i>
                  </li>
                ))}
              </ul>
            </div>

            {/* Quick Contact */}
            <div className="bg-gray-900 rounded-lg p-6 text-white">
              <p className="text-sm font-semibold mb-1 text-white">Need to reach us?</p>
              <p className="text-xs text-gray-400 mb-5 leading-relaxed">
                Our team is available 7 days a week to help you.
              </p>
              <a
                href="tel:9057099985"
                className="flex items-center gap-2.5 text-sm text-gray-300 hover:text-white transition-colors cursor-pointer mb-3"
              >
                <i className="ri-phone-line text-teal-400"></i>
                (905) 709-9985
              </a>
              <a
                href="mailto:info@thornhilltreatment.com"
                className="flex items-center gap-2.5 text-sm text-gray-300 hover:text-white transition-colors cursor-pointer mb-3"
              >
                <i className="ri-mail-line text-teal-400"></i>
                info@thornhilltreatment.com
              </a>
              <a
                href="https://www.google.com/maps/dir/?api=1&destination=8108+Yonge+St+Unit+3-4,+Thornhill,+ON+L4J+1W4"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2.5 text-sm text-gray-300 hover:text-white transition-colors cursor-pointer"
              >
                <i className="ri-navigation-line text-teal-400"></i>
                Get Directions
              </a>
            </div>

            {/* CTA Card */}
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="w-full h-36 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=welcoming%20pharmacy%20storefront%20exterior%20modern%20building%20Thornhill%20Ontario%20professional%20healthcare%20facility%20bright%20daytime%20clean%20signage%20accessible%20entrance%20inviting&width=400&height=200&seq=loc-sidebar-cta-v2&orientation=landscape"
                  alt="Thornhill Drug-Mart exterior"
                  className="w-full h-full object-cover object-top"
                  width={400}
                  height={160}
                />
              </div>
              <div className="p-5">
                <h3 className="text-sm font-semibold text-gray-900 mb-1">Walk-Ins Welcome</h3>
                <p className="text-xs text-gray-500 mb-4 leading-relaxed">
                  No appointment needed — come in anytime during operating hours.
                </p>
                <a
                  href="tel:9057099985"
                  className="block text-center bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold py-2.5 rounded-md transition-colors cursor-pointer whitespace-nowrap"
                >
                  Call Ahead
                </a>
              </div>
            </div>

          </aside>
        </div>
      </div>

      <Footer />
    </div>
  );
}
